// Static catalog of game definitions. Pure data — no scoring logic, no
// state. Runners (per-game compute functions) live alongside this in
// runners/<gameId>.ts and import from here.
//
// Each game entry is the spec we agreed on: identity (id, name,
// category, scoringType, requirement flags, description) plus the
// configurable options that vary between groups, plus dependencies on
// other games or score modes. Adding a new game = appending one record
// here and (if interactive) shipping a runner.

export type Category = "individual" | "team" | "side" | "meta";

export type ScoringType =
  | "perHole"
  | "points"
  | "segment"
  | "rotation"
  | "tracking";

export type OptionType = "boolean" | "number" | "select";

export interface OptionChoice {
  value: string;
  label: string;
}

export interface GameOption {
  key: string;
  type: OptionType;
  default: boolean | number | string;
  label: string;
  // For type === "select"
  choices?: OptionChoice[];
  // For type === "number"
  min?: number;
  max?: number;
  step?: number;
  // For type === "number" — an explicit value list for the scroll picker,
  // overriding min/max/step. Used for non-uniform bet sets (e.g. a sub-dollar
  // ramp [0, 0.25, 0.5, 1, 2, 3, …]).
  values?: number[];
  // For type === "number" — render the value 0 as this string in both
  // the trigger and the picker wheel (e.g. "—" for a "no bet" feel on
  // money inputs). The stored value is still numeric 0.
  zeroLabel?: string;
  // Force this option into the advanced "Options ▾" disclosure even
  // when its type would normally surface as a primary inline field.
  advanced?: boolean;
  // Conditional visibility — only show this option when another
  // option's value matches. Used for Nassau front/back bets that
  // only make sense when the Nassau checkbox is ticked. The value
  // can be a single match (`value: "..."`) or a list
  // (`value: ["a", "b"]`) so an option can be visible across
  // multiple parent states — used by Hollywood for sub-game-gated
  // options that apply to more than one sub-game.
  dependsOn?: {
    key: string;
    value:
      | boolean
      | number
      | string
      | Array<boolean | number | string>;
  };
  // Optional render override for `select` options. "radio" renders
  // the choices as side-by-side radio buttons (no header) instead of
  // a dropdown — used for two-choice toggles where seeing both
  // values at once reads more naturally than a hidden dropdown.
  renderAs?: "radio";
  // Hide this option unless the card has at least this many players.
  // Used for the 2-v-2 Team controls, which are meaningless below a
  // foursome — the checkbox + its dependent team setup/scoring fields
  // all stay hidden until a 4th player is added.
  minPlayers?: number;
  // Optional explainer surfaced via an ℹ button beside the option's label,
  // expanding inline on tap. Used for options whose effect on the leaderboard
  // isn't obvious (e.g. Entry Fee Paid).
  info?: string;
}

export type DependencyKind = "wraps" | "augments" | "requires";

export interface GameDependency {
  kind: DependencyKind;
  // Game id, or score-mode token like "score:gross" / "score:net" /
  // "score:stableford" — these don't reference a game id but assert
  // the round must be tracking that score type for the game to compute.
  target: string;
  // Option key driving the dependency, when applicable (e.g. subGame).
  via?: string;
  note?: string;
}

export interface GameDefinition {
  id: string;
  name: string;
  category: Category;
  scoringType: ScoringType;
  requiresHandicap: boolean;
  requiresTeams: boolean;
  minPlayers?: number;
  maxPlayers?: number;
  description: string;
  options: GameOption[];
  dependencies?: GameDependency[];
  // Mandatory games are auto-enabled on every round and cannot be
  // toggled off in the Games modal. Used for the foundational bet
  // (Stroke Play) that every round assumes is in play.
  mandatory?: boolean;
}

// ───────── Catalog ─────────
//
// Order here drives the Games modal's list order — categories no longer
// cluster (e.g. Hi-Lo and Hollywood sit between side-game entries) so
// section comments were dropped in favour of a single user-curated
// sequence.

export const GAMES_CATALOG: GameDefinition[] = [
  {
    id: "strokePlay",
    name: "Stroke Play",
    category: "individual",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: false,
    mandatory: true,
    description:
      "AKA Medalplay. Lowest total strokes wins. Choose Bet per Player (round-long pot, lowest gross wins) or Bet per Point (head-to-head: each player wins (opponent's strokes − their strokes) × bet from every opponent). Tick Nassau to play front-9, back-9, and overall pots — independent stakes per segment. Enable Team to combine two players per side and settle the bet against the opposing team's total.",
    options: [
      // See the matching Stableford block for layout notes — the same
      // three-column bet grid (Bet per Player | empty | Bet per Point)
      // renders for Stroke Play too, swapping to Overall / Front 9 /
      // Back 9 when Nassau ticks on. The five bet keys below feed the
      // grid; useBetPerPoint picks which non-Nassau branch settles.
      {
        key: "overallBet",
        type: "number",
        default: 10,
        label: "Bet per Player",
        min: 0,
        zeroLabel: "—",
        advanced: true,
      },
      {
        key: "betPerPoint",
        type: "number",
        default: 0,
        label: "Bet per Point",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: false },
      },
      {
        key: "useBetPerPoint",
        type: "boolean",
        default: false,
        label: "Use bet per point instead of bet per player",
        advanced: true,
        dependsOn: { key: "nassau", value: false },
      },
      {
        key: "frontNineBet",
        type: "number",
        default: 5,
        label: "Front 9 bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "backNineBet",
        type: "number",
        default: 5,
        label: "Back 9 bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "nassau",
        type: "boolean",
        default: false,
        label: "Nassau (front 9 + back 9 sub-bets)",
      },
      {
        key: "team",
        type: "boolean",
        default: false,
        label: "Team (combine pairs of players for all bets)",
        minPlayers: 4,
      },
      {
        key: "teamSetup",
        type: "select",
        default: "1-2v3-4",
        label: "Team setup",
        choices: [
          { value: "1-2v3-4", label: "Player 1 & 2 vs Player 3 & 4" },
          { value: "1-3v2-4", label: "Player 1 & 3 vs Player 2 & 4" },
          { value: "1-4v2-3", label: "Player 1 & 4 vs Player 2 & 3" },
        ],
        advanced: true,
        dependsOn: { key: "team", value: true },
      },
      {
        key: "teamFormat",
        type: "select",
        default: "aggregate",
        label: "Team scoring",
        choices: [
          { value: "aggregate", label: "Aggregate" },
          { value: "betterBall", label: "Better Ball" },
        ],
        advanced: true,
        dependsOn: { key: "team", value: true },
        renderAs: "radio",
      },
    ],
  },
  {
    id: "matchPlay",
    name: "Match Play",
    category: "individual",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: false,
    description:
      "Head-to-head match: win a hole outright to go 1 Up, a tie halves it, and the player (or team) ahead after the segment wins the bet. Tick Nassau for separate front-9, back-9, and overall matches. With four players, enable Team for a 2-v-2 match, or Individual matches to split the fourball into two separate 1-v-1 matches (e.g. 1-v-2 and 3-v-4).",
    options: [
      {
        key: "overallBet",
        type: "number",
        default: 10,
        label: "Bet per Player",
        min: 0,
        zeroLabel: "—",
        advanced: true,
      },
      {
        key: "frontNineBet",
        type: "number",
        default: 5,
        label: "Front 9 bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "backNineBet",
        type: "number",
        default: 5,
        label: "Back 9 bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "nassau",
        type: "boolean",
        default: false,
        label: "Nassau (front 9 + back 9 sub-matches)",
      },
      {
        key: "team",
        type: "boolean",
        default: false,
        label: "Team — 2 v 2 (needs 4 players)",
        minPlayers: 4,
      },
      {
        key: "teamSetup",
        type: "select",
        default: "1-2v3-4",
        label: "Team setup",
        choices: [
          { value: "1-2v3-4", label: "Player 1 & 2 vs Player 3 & 4" },
          { value: "1-3v2-4", label: "Player 1 & 3 vs Player 2 & 4" },
          { value: "1-4v2-3", label: "Player 1 & 4 vs Player 2 & 3" },
        ],
        advanced: true,
        dependsOn: { key: "team", value: true },
      },
      {
        key: "teamFormat",
        type: "select",
        default: "betterBall",
        label: "Team scoring",
        choices: [
          { value: "betterBall", label: "Better Ball" },
          { value: "aggregate", label: "Aggregate" },
        ],
        advanced: true,
        renderAs: "radio",
        dependsOn: { key: "team", value: true },
      },
      {
        key: "dualMatch",
        type: "boolean",
        default: false,
        label: "Individual matches — two 1 v 1 (needs 4 players)",
        minPlayers: 4,
      },
      {
        key: "dualMatchSetup",
        type: "select",
        default: "1v2-3v4",
        label: "Match pairings",
        choices: [
          { value: "1v2-3v4", label: "Match 1: P1 v P2 · Match 2: P3 v P4" },
          { value: "1v3-2v4", label: "Match 1: P1 v P3 · Match 2: P2 v P4" },
          { value: "1v4-2v3", label: "Match 1: P1 v P4 · Match 2: P2 v P3" },
        ],
        advanced: true,
        dependsOn: { key: "dualMatch", value: true },
      },
      {
        key: "scoringMode",
        type: "select",
        default: "gross",
        label: "Score basis",
        choices: [
          { value: "gross", label: "Gross" },
          { value: "net", label: "Net" },
        ],
        renderAs: "radio",
      },
      {
        key: "handicapMode",
        type: "select",
        default: "full",
        label: "Handicap mode",
        choices: [
          { value: "full", label: "Full Handicap" },
          { value: "playOffLow", label: "Play off Low" },
        ],
        renderAs: "radio",
        dependsOn: { key: "scoringMode", value: "net" },
      },
    ],
  },
  {
    id: "stableford",
    name: "Stableford",
    category: "individual",
    scoringType: "points",
    requiresHandicap: true,
    requiresTeams: false,
    description:
      "Net score vs net par yields per-hole points. Choose Bet per Player (round-long pot, highest points wins) or Bet per Point (head-to-head: each player wins (their points − opponent's points) × bet from every opponent). Tick Nassau to play front-9, back-9, and overall pots — independent stakes per segment. Enable Team to combine two players per side and settle the bet against the opposing team's total.",
    options: [
      // Bet pair: "Bet per Player" (overallBet — pot-style ante that
      // pays the round winner) sits beside "Bet per Point" (per-point
      // head-to-head transfers between every player pair). The toggle
      // below picks which one fires. Both inputs stay visible at the
      // same time so the player can see what each value would do
      // before flipping the toggle. The Bet-per-Player label
      // automatically renames to "Overall bet" when Nassau is ticked
      // (see resolveOptionLabel in GamesModal) — Nassau brings back
      // the three-pot model and Overall is the top-level pot of the
      // trio.
      {
        key: "overallBet",
        type: "number",
        default: 10,
        label: "Bet per Player",
        min: 0,
        zeroLabel: "—",
        advanced: true,
      },
      {
        key: "betPerPoint",
        type: "number",
        default: 1,
        label: "Bet per Point",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: false },
      },
      {
        key: "useBetPerPoint",
        type: "boolean",
        default: false,
        label: "Use bet per point instead of bet per player",
        advanced: true,
        dependsOn: { key: "nassau", value: false },
      },
      {
        key: "frontNineBet",
        type: "number",
        default: 5,
        label: "Front 9 bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "backNineBet",
        type: "number",
        default: 5,
        label: "Back 9 bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "nassau",
        type: "boolean",
        default: false,
        label: "Nassau (front 9 + back 9 sub-bets)",
      },
      {
        key: "team",
        type: "boolean",
        default: false,
        label: "Team (combine pairs of players for all bets)",
        minPlayers: 4,
      },
      {
        key: "teamSetup",
        type: "select",
        default: "1-2v3-4",
        label: "Team setup",
        choices: [
          { value: "1-2v3-4", label: "Player 1 & 2 vs Player 3 & 4" },
          { value: "1-3v2-4", label: "Player 1 & 3 vs Player 2 & 4" },
          { value: "1-4v2-3", label: "Player 1 & 4 vs Player 2 & 3" },
        ],
        advanced: true,
        dependsOn: { key: "team", value: true },
      },
      {
        key: "teamFormat",
        type: "select",
        default: "aggregate",
        label: "Team scoring",
        choices: [
          { value: "aggregate", label: "Aggregate" },
          { value: "betterBall", label: "Better Ball" },
        ],
        advanced: true,
        renderAs: "radio",
        dependsOn: { key: "team", value: true },
      },
      {
        key: "scoringScale",
        type: "select",
        default: "standard",
        label: "Points scale",
        choices: [
          {
            value: "standard",
            label: "Standard: 1 bogey / 2 par / 3 birdie / 4 eagle / 5 albatross",
          },
          {
            value: "modified",
            label:
              "Modified: 1 dbl-bogey / 2 bogey / 3 par / 4 birdie / 5 eagle / 6 albatross",
          },
          {
            value: "modifiedPga",
            label:
              "Modified (PGA): -1 bogey / 0 par / +2 birdie / +5 eagle / +8 albatross",
          },
        ],
      },
      {
        key: "handicapMode",
        type: "select",
        default: "full",
        label: "Handicap mode",
        choices: [
          { value: "full", label: "Full Handicap" },
          { value: "playOffLow", label: "Play off Low" },
        ],
        renderAs: "radio",
      },
    ],
  },
  {
    id: "skins",
    name: "Skins",
    category: "side",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: false,
    description:
      "Lowest outright score on each hole wins the skin. In pot mode each player puts in a fixed bet and the pot is split among the won holes. In carry-forward mode each hole has its own bet that rolls into the next hole on a tie. Gross and net are scored as parallel games.",
    options: [
      {
        key: "mode",
        type: "select",
        default: "pot",
        label: "Mode",
        choices: [
          { value: "pot", label: "Pot" },
          { value: "carryForward", label: "Carry forward" },
        ],
        renderAs: "radio",
      },
      // Two pairs of bet inputs, one pair per mode. Only the pair for
      // the active mode is visible (`dependsOn` filters the rest), so
      // switching modes preserves the inactive pair's values for the
      // user without polluting the rendered UI.
      {
        key: "potBetGross",
        type: "number",
        default: 5,
        label: "Each player bet (gross)",
        min: 0,
        advanced: true,
        dependsOn: { key: "mode", value: "pot" },
      },
      {
        key: "potBetNet",
        type: "number",
        default: 5,
        label: "Each player bet (net)",
        min: 0,
        advanced: true,
        dependsOn: { key: "mode", value: "pot" },
      },
      {
        key: "carryBetGross",
        type: "number",
        default: 1,
        label: "Player bet per hole (gross)",
        min: 0,
        advanced: true,
        dependsOn: { key: "mode", value: "carryForward" },
      },
      {
        key: "carryBetNet",
        type: "number",
        default: 1,
        label: "Player bet per hole (net)",
        min: 0,
        advanced: true,
        dependsOn: { key: "mode", value: "carryForward" },
      },
    ],
  },
  {
    id: "ooslerWoosler",
    name: "Oosler / Woosler",
    category: "side",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: false,
    description:
      "Par-3 only. Closest tee shot on the green (Oosler) wins a point from each player by making par. If he fails (Woosler) pays 1 point to each opposing player. Birdies from other players steal the points, if multiple birdies, last one steals. If no one's on the green in 1, points carry to the next par 3. Bet per point scales the unit-point transfers into currency.",
    options: [
      {
        key: "pointBet",
        type: "number",
        default: 1,
        label: "Bet per point",
        min: 0,
        zeroLabel: "—",
        advanced: true,
      },
    ],
    dependencies: [
      {
        kind: "requires",
        target: "score:gross",
        note: "Par/birdie detection needs gross score.",
      },
    ],
  },
  {
    id: "putting",
    name: "Putting",
    category: "side",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: false,
    description:
      "Side game tracking putts per hole. Chip-ins or off green putts count as zero-putts for 2 points, 1-putts get 1 point, 3-putts award one point to opposing players. Pickups count as 3 putts. Up and down from bunker for a par gets bonus point. Choose Bet per Player (pot, highest total points wins) or Bet per Point (head-to-head per-point transfers). Tick Nassau for front-9, back-9, and overall sub-bets.",
    // Bunker save scoring is additive — an up-and-down par out of a
    // bunker totals 2 points (1 for the bunker save + 1 for the 1-putt
    // that closed it out). Same bet-mode grid as Stableford / Stroke
    // Play: overallBet doubles as the non-Nassau pot ("Bet per Player")
    // and the Nassau overall pot; pointBet is the non-Nassau per-point
    // rate; useBetPerPoint picks which branch settles.
    options: [
      {
        key: "overallBet",
        type: "number",
        default: 10,
        label: "Bet per Player",
        min: 0,
        zeroLabel: "—",
        advanced: true,
      },
      {
        key: "betPerPoint",
        type: "number",
        default: 1,
        label: "Bet per Point",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: false },
      },
      {
        key: "useBetPerPoint",
        type: "boolean",
        default: true,
        label: "Use bet per point instead of bet per player",
        advanced: true,
        dependsOn: { key: "nassau", value: false },
      },
      {
        key: "frontNineBet",
        type: "number",
        default: 5,
        label: "Front 9 bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "backNineBet",
        type: "number",
        default: 5,
        label: "Back 9 bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "nassau",
        type: "boolean",
        default: false,
        label: "Nassau (front 9 + back 9 + overall pots)",
      },
    ],
  },
  {
    id: "acesAndDeuces",
    name: "Aces and Deuces",
    category: "side",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: false,
    description:
      "Per hole the outright low score (\"ace\") earns 1 point from each other player; the outright high score (\"deuce\") loses 1 point to each other player. Ties for low or high produce no movement. Choose Bet per Player (round-long pot, most points wins) or Bet per Point (head-to-head per-point transfers). Tick Nassau for front-9, back-9, and overall sub-bets.",
    // Bet block (overallBet / betPerPoint / useBetPerPoint + Nassau
    // segment bets) flows through the shared bet-mode grid so the
    // inputs sit at the top of the gear panel under either Nassau
    // state. Per-hole point allocation is fixed (1 to the unique low /
    // -1 from the unique high) — only score basis is configurable.
    options: [
      {
        key: "overallBet",
        type: "number",
        default: 10,
        label: "Bet per Player",
        min: 0,
        zeroLabel: "—",
        advanced: true,
      },
      {
        key: "betPerPoint",
        type: "number",
        default: 1,
        label: "Bet per Point",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: false },
      },
      {
        key: "useBetPerPoint",
        type: "boolean",
        default: true,
        label: "Use bet per point instead of bet per player",
        advanced: true,
        dependsOn: { key: "nassau", value: false },
      },
      {
        key: "frontNineBet",
        type: "number",
        default: 5,
        label: "Front 9 bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "backNineBet",
        type: "number",
        default: 5,
        label: "Back 9 bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "nassau",
        type: "boolean",
        default: false,
        label: "Nassau (front 9 + back 9 + overall pots)",
      },
      {
        key: "scoringMode",
        type: "select",
        default: "gross",
        label: "Score basis",
        choices: [
          { value: "gross", label: "Gross" },
          { value: "net", label: "Net" },
        ],
        advanced: true,
        renderAs: "radio",
      },
      {
        key: "handicapMode",
        type: "select",
        default: "full",
        label: "Handicap mode",
        choices: [
          { value: "full", label: "Full Handicap" },
          { value: "playOffLow", label: "Play off Low" },
        ],
        advanced: true,
        renderAs: "radio",
        dependsOn: { key: "scoringMode", value: "net" },
      },
    ],
  },
  {
    id: "hiLo",
    name: "Hi-Lo",
    category: "team",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: true,
    minPlayers: 4,
    maxPlayers: 4,
    description:
      "4-ball team game. Lower-ball point goes to the team with the better low score on the hole; higher-ball point goes to the team with the better high score. Tied scores split the point. Choose Bet per Player (round-long pot, team with more total points wins) or Bet per Point (head-to-head per-point transfers between the two teams).",
    // Option order (top → bottom): bet block (overallBet / betPerPoint
    // / useBetPerPoint flow through the bet-mode grid), team setup,
    // handicap mode radio, score basis radio, auto-press toggle.
    // Hi-Lo has no Nassau so the grid always renders in its two-column
    // non-Nassau layout.
    options: [
      {
        key: "overallBet",
        type: "number",
        default: 10,
        label: "Bet per Player",
        min: 0,
        zeroLabel: "—",
        advanced: true,
      },
      {
        key: "betPerPoint",
        type: "number",
        default: 1,
        label: "Bet per Point",
        min: 0,
        zeroLabel: "—",
        advanced: true,
      },
      {
        key: "useBetPerPoint",
        type: "boolean",
        default: true,
        label: "Use bet per point instead of bet per player",
        advanced: true,
      },
      // Hi-Lo is always team-based, so the catalog skips the on/off
      // Team checkbox that Stroke Play / Stableford carry. The
      // pairing dropdown surfaces directly with the same three
      // choices we use everywhere else.
      {
        key: "teamSetup",
        type: "select",
        default: "1-2v3-4",
        label: "Team setup",
        choices: [
          { value: "1-2v3-4", label: "Player 1 & 2 vs Player 3 & 4" },
          { value: "1-3v2-4", label: "Player 1 & 3 vs Player 2 & 4" },
          { value: "1-4v2-3", label: "Player 1 & 4 vs Player 2 & 3" },
        ],
      },
      {
        key: "handicapMode",
        type: "select",
        default: "full",
        label: "Handicap mode",
        choices: [
          { value: "full", label: "Full Handicap" },
          { value: "playOffLow", label: "Play off Low" },
        ],
        renderAs: "radio",
      },
      {
        key: "scoringMode",
        type: "select",
        default: "net",
        label: "Score basis",
        choices: [
          { value: "net", label: "Net" },
          { value: "gross", label: "Gross" },
        ],
        renderAs: "radio",
      },
      {
        key: "autoPress",
        type: "boolean",
        default: false,
        label: "Auto press triggers when a team goes 2 up in the match",
      },
    ],
  },
  {
    id: "vegas",
    name: "Vegas",
    category: "team",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: true,
    minPlayers: 4,
    maxPlayers: 4,
    description:
      "Each two-player team's hole score is the two team scores combined as digits (lower first). e.g. 3 + 5 = 35. Points awarded by the difference between the two team numbers. Strictly a points game — the bet rate scales each unit of differential into currency.",
    options: [
      {
        key: "pointBet",
        type: "number",
        default: 1,
        label: "Bet per point differential",
        min: 0,
        zeroLabel: "—",
        advanced: true,
      },
      {
        key: "scoringMode",
        type: "select",
        default: "gross",
        label: "Score basis",
        choices: [
          { value: "gross", label: "Gross" },
          { value: "net", label: "Net" },
        ],
        renderAs: "radio",
      },
      // Vegas is always team-based, so no on/off Team checkbox —
      // the pairing dropdown surfaces directly, same shape as Hi-Lo.
      {
        key: "teamSetup",
        type: "select",
        default: "1-2v3-4",
        label: "Team setup",
        choices: [
          { value: "1-2v3-4", label: "Player 1 & 2 vs Player 3 & 4" },
          { value: "1-3v2-4", label: "Player 1 & 3 vs Player 2 & 4" },
          { value: "1-4v2-3", label: "Player 1 & 4 vs Player 2 & 3" },
        ],
      },
      {
        key: "flipOnBogey",
        type: "boolean",
        default: true,
        label:
          "If a player on the team makes bogey or worse, the team's lower digit moves to the higher position (penalty flip)",
      },
      {
        key: "doubleOnBirdie",
        type: "boolean",
        default: false,
        label: "Double the per-hole differential when a player makes birdie",
      },
    ],
  },
  {
    id: "hollywood",
    name: "Hollywood (Six-Six-Six / Round Robin)",
    category: "meta",
    scoringType: "rotation",
    requiresHandicap: false,
    requiresTeams: true,
    minPlayers: 4,
    maxPlayers: 4,
    description:
      "4-ball meta-game. Three 6-hole matches with a random two-player pairing each segment, so every player partners with every other player exactly once. Pick the sub-game (Stroke Play / Stableford / Hi-Lo / Vegas) and each segment settles using that game's team logic. Pairings are randomised when Hollywood is first enabled.",
    // Rotation is fixed at six holes per segment — it's literally in the
    // game's name. Pairings (pairing1/2/3) are seeded with a random
    // round-robin permutation when Hollywood is first enabled (see
    // defaultsWithHandicapAwareScoring in GamesModal), so the user
    // doesn't manually pick them. The sub-game selection drives the
    // settlement and surfaces a tailored subset of bet/format options.
    options: [
      {
        key: "subGame",
        type: "select",
        default: "strokePlay",
        label: "Sub-game",
        choices: [
          { value: "strokePlay", label: "Stroke Play" },
          { value: "stableford", label: "Stableford" },
          { value: "hiLo", label: "Hi-Lo" },
          { value: "vegas", label: "Vegas" },
        ],
      },

      // ── Bet block (shared by Stroke Play / Stableford / Hi-Lo) ─────
      // Same overallBet / betPerPoint / useBetPerPoint trio every
      // other game uses. Routed through the bet-mode grid in GamesModal
      // so the user picks one with the side-by-side radios.
      {
        key: "overallBet",
        type: "number",
        default: 10,
        label: "Bet per Player",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: {
          key: "subGame",
          value: ["strokePlay", "stableford", "hiLo"],
        },
      },
      {
        key: "betPerPoint",
        type: "number",
        default: 1,
        label: "Bet per Point",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: {
          key: "subGame",
          value: ["strokePlay", "stableford", "hiLo"],
        },
      },
      {
        key: "useBetPerPoint",
        type: "boolean",
        default: true,
        label: "Use bet per point instead of bet per player",
        advanced: true,
        dependsOn: {
          key: "subGame",
          value: ["strokePlay", "stableford", "hiLo"],
        },
      },

      // ── Team scoring (Stroke Play + Stableford only) ───────────────
      {
        key: "teamFormat",
        type: "select",
        default: "aggregate",
        label: "Team scoring",
        choices: [
          { value: "aggregate", label: "Aggregate" },
          { value: "betterBall", label: "Better Ball" },
        ],
        advanced: true,
        renderAs: "radio",
        dependsOn: {
          key: "subGame",
          value: ["strokePlay", "stableford"],
        },
      },

      // ── Stableford-only options ────────────────────────────────────
      {
        key: "scoringScale",
        type: "select",
        default: "standard",
        label: "Points scale",
        choices: [
          {
            value: "standard",
            label: "Standard: 1 bogey / 2 par / 3 birdie / 4 eagle / 5 albatross",
          },
          {
            value: "modified",
            label:
              "Modified: 1 dbl-bogey / 2 bogey / 3 par / 4 birdie / 5 eagle / 6 albatross",
          },
          {
            value: "modifiedPga",
            label:
              "Modified (PGA): -1 bogey / 0 par / +2 birdie / +5 eagle / +8 albatross",
          },
        ],
        advanced: true,
        dependsOn: { key: "subGame", value: "stableford" },
      },

      // ── Handicap mode (Stableford + Hi-Lo) ─────────────────────────
      {
        key: "handicapMode",
        type: "select",
        default: "full",
        label: "Handicap mode",
        choices: [
          { value: "full", label: "Full Handicap" },
          { value: "playOffLow", label: "Play off Low" },
        ],
        advanced: true,
        renderAs: "radio",
        dependsOn: {
          key: "subGame",
          value: ["stableford", "hiLo"],
        },
      },

      // ── Score basis (Hi-Lo + Vegas) ────────────────────────────────
      {
        key: "scoringMode",
        type: "select",
        default: "net",
        label: "Score basis",
        choices: [
          { value: "net", label: "Net" },
          { value: "gross", label: "Gross" },
        ],
        advanced: true,
        renderAs: "radio",
        dependsOn: {
          key: "subGame",
          value: ["hiLo", "vegas"],
        },
      },

      // ── Hi-Lo-only options ─────────────────────────────────────────
      {
        key: "autoPress",
        type: "boolean",
        default: false,
        label: "Auto press triggers when a team goes 2 up in the match",
        advanced: true,
        dependsOn: { key: "subGame", value: "hiLo" },
      },

      // ── Starting hole (shotgun start) — applies to every sub-game ──
      // For a shotgun start the group may begin on a hole other than 1,
      // so the three 6-hole segments rotate from the starting hole and
      // wrap past 18 (e.g. start on 10 → 10–15 / 16–3 / 4–9).
      {
        key: "changeStartHole",
        type: "boolean",
        default: false,
        label: "Change starting hole",
        advanced: true,
      },
      {
        key: "startHole",
        type: "number",
        default: 1,
        label: "Starting hole",
        min: 1,
        max: 18,
        values: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18],
        advanced: true,
        dependsOn: { key: "changeStartHole", value: true },
      },

      // ── Vegas-only options ─────────────────────────────────────────
      {
        key: "pointBet",
        type: "number",
        default: 1,
        label: "Bet per point differential",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "subGame", value: "vegas" },
      },
      {
        key: "flipOnBogey",
        type: "boolean",
        default: true,
        label:
          "If a player on the team makes bogey or worse, the team's lower digit moves to the higher position (penalty flip)",
        advanced: true,
        dependsOn: { key: "subGame", value: "vegas" },
      },
      {
        key: "doubleOnBirdie",
        type: "boolean",
        default: false,
        label: "Double the per-hole differential when a player makes birdie",
        advanced: true,
        dependsOn: { key: "subGame", value: "vegas" },
      },

      // ── Pairings (hidden — auto-randomised on first enable) ────────
      // Stored for the runner; not surfaced in the UI any more.
      {
        key: "pairing1",
        type: "select",
        default: "1-2v3-4",
        label: "Holes 1–6 pairing",
        choices: [
          { value: "1-2v3-4", label: "1 & 2 vs 3 & 4" },
          { value: "1-3v2-4", label: "1 & 3 vs 2 & 4" },
          { value: "1-4v2-3", label: "1 & 4 vs 2 & 3" },
        ],
        advanced: true,
        // Permanently hidden — set indirectly by the auto-shuffle on
        // first enable. dependsOn pointed at a key that's always
        // null keeps the row out of the rendered list without needing
        // a new schema flag.
        dependsOn: { key: "__hidden__", value: "yes" },
      },
      {
        key: "pairing2",
        type: "select",
        default: "1-3v2-4",
        label: "Holes 7–12 pairing",
        choices: [
          { value: "1-2v3-4", label: "1 & 2 vs 3 & 4" },
          { value: "1-3v2-4", label: "1 & 3 vs 2 & 4" },
          { value: "1-4v2-3", label: "1 & 4 vs 2 & 3" },
        ],
        advanced: true,
        dependsOn: { key: "__hidden__", value: "yes" },
      },
      {
        key: "pairing3",
        type: "select",
        default: "1-4v2-3",
        label: "Holes 13–18 pairing",
        choices: [
          { value: "1-2v3-4", label: "1 & 2 vs 3 & 4" },
          { value: "1-3v2-4", label: "1 & 3 vs 2 & 4" },
          { value: "1-4v2-3", label: "1 & 4 vs 2 & 3" },
        ],
        advanced: true,
        dependsOn: { key: "__hidden__", value: "yes" },
      },
    ],
  },
  {
    id: "wolf",
    name: "Wolf",
    category: "side",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: false,
    minPlayers: 4,
    maxPlayers: 4,
    description:
      "Four-player game where the Wolf rotates each hole and tees off first. After each player's tee shot, the Wolf may immediately choose that player as a partner or continue waiting. If no partner is selected before the final player's shot, the Wolf must either choose the final player as a partner or play as a Lone Wolf against the other three players. The Wolf may also declare Blind Wolf before any player tees off, committing to play alone against the other three players for an increased payout multiplier.",
    // Each hole the Wolf picks Lone Wolf / Blind Wolf / a partner via the
    // live per-hole picker (fires when the first score is entered). Sides are
    // compared by better-ball (low score, gross or net). A win collects
    // betPerPlayer from each opponent; ties push. Blind Wolf (declared solo
    // before any tee shot) multiplies that hole's settlement.
    options: [
      {
        key: "betPerPlayer",
        type: "number",
        default: 1,
        label: "Bet per player",
        min: 0,
        zeroLabel: "—",
        advanced: true,
      },
      {
        key: "blindEnabled",
        type: "boolean",
        default: true,
        label: "Blind Wolf Multiplier",
      },
      {
        key: "blindMultiplier",
        type: "select",
        default: "2",
        label: "Multiplier",
        choices: [
          { value: "2", label: "2×" },
          { value: "3", label: "3×" },
          { value: "4", label: "4×" },
          { value: "5", label: "5×" },
        ],
        dependsOn: { key: "blindEnabled", value: true },
      },
      {
        key: "scoringMode",
        type: "select",
        default: "gross",
        label: "Score basis",
        choices: [
          { value: "gross", label: "Gross" },
          { value: "net", label: "Net" },
        ],
        renderAs: "radio",
      },
      {
        key: "handicapMode",
        type: "select",
        default: "full",
        label: "Handicap mode",
        choices: [
          { value: "full", label: "Full Handicap" },
          { value: "playOffLow", label: "Play off Low" },
        ],
        renderAs: "radio",
        dependsOn: { key: "scoringMode", value: "net" },
      },
    ],
  },
  {
    id: "rabbit",
    name: "Rabbit",
    category: "side",
    scoringType: "segment",
    requiresHandicap: false,
    requiresTeams: false,
    description:
      "First player to win a hole outright captures the Rabbit. Tied holes do not change ownership. The player holding the Rabbit after 9 wins front 9 pot, then Rabbit resets and the player holding the Rabbit after 18 wins the back 9 pot.",
    // Single Bet per Player ante; the round's total is split into a front-9 pot
    // and a back-9 pot (e.g. $10/player × 4 players = $40 → $20 each pot). Whole
    // pot goes to the Rabbit-holder at hole 9 / hole 18.
    options: [
      {
        key: "betPerPlayer",
        type: "number",
        default: 10,
        label: "Bet per player",
        min: 0,
        zeroLabel: "—",
        advanced: true,
      },
      {
        key: "scoringMode",
        type: "select",
        default: "gross",
        label: "Score basis",
        choices: [
          { value: "gross", label: "Gross" },
          { value: "net", label: "Net" },
        ],
        renderAs: "radio",
      },
      {
        key: "handicapMode",
        type: "select",
        default: "full",
        label: "Handicap mode",
        choices: [
          { value: "full", label: "Full Handicap" },
          { value: "playOffLow", label: "Play off Low" },
        ],
        renderAs: "radio",
        dependsOn: { key: "scoringMode", value: "net" },
      },
    ],
  },
  {
    id: "bingoBangoBongo",
    name: "Bingo, Bango, Bongo",
    category: "side",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: false,
    description:
      "Three points per hole: Bingo (first ball on the green), Bango (closest to the pin once all balls are on the green), Bongo (first ball in the cup). The hole's three winners are picked live; each point is worth the bet from every opponent.",
    options: [
      {
        key: "betPerPoint",
        type: "number",
        default: 0.25,
        label: "Bet per point",
        min: 0,
        zeroLabel: "—",
        values: [0, 0.25, 0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20],
        advanced: true,
      },
    ],
  },
  {
    id: "bounceBack",
    name: "Bounce Back",
    category: "individual",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: false,
    description:
      "Earn for following a double bogey or worse with a par or better. Earn 1 from each player when player follows a double bogey or worse with a par. Earn Double when followed with a birdie or better. Pay each other player 1 if player makes back-to-back double bogeys or worse.",
    // Gross-only, per-opponent transfer at `betPerPoint`:
    //   • par after a double bogey or worse  → +1 from each opponent
    //   • birdie-or-better after dbl+         → +2 from each opponent
    //   • back-to-back double bogey or worse  → −1 to each opponent
    options: [
      {
        key: "betPerPoint",
        type: "number",
        default: 1,
        label: "Bet per point",
        min: 0,
        zeroLabel: "—",
        values: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 15, 20],
        advanced: true,
      },
    ],
    dependencies: [
      {
        kind: "requires",
        target: "score:gross",
        note: "Detection of bogey/double bogey needs gross score vs hole par.",
      },
    ],
  },
  {
    id: "nines",
    name: "Nines (5-3-1)",
    category: "side",
    scoringType: "perHole",
    requiresHandicap: false,
    requiresTeams: false,
    minPlayers: 3,
    maxPlayers: 3,
    description:
      "3-ball game. Nine points are split per hole — outright winner 5, second 3, third 1. Tie for second splits 5/2/2; tie for first splits 4/4/1; three-way tie splits 3/3/3. Choose Bet per point to settle at the chosen rate per point, or tick Nassau to split front-9, back-9, and overall pots based on segment point totals.",
    options: [
      {
        key: "scoringMode",
        type: "select",
        default: "net",
        label: "Score basis",
        choices: [
          { value: "net", label: "Net" },
          { value: "gross", label: "Gross" },
          { value: "netGross", label: "Net & Gross" },
        ],
      },
      // Bet-per-point mode (default — Nassau unticked).
      {
        key: "pointBet",
        type: "number",
        default: 1,
        label: "Bet per point",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: false },
      },
      // Nassau pots — same shape as Putting / Stroke Play.
      {
        key: "overallBet",
        type: "number",
        default: 5,
        label: "Overall bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "frontNineBet",
        type: "number",
        default: 5,
        label: "Front 9 bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "backNineBet",
        type: "number",
        default: 5,
        label: "Back 9 bet",
        min: 0,
        zeroLabel: "—",
        advanced: true,
        dependsOn: { key: "nassau", value: true },
      },
      {
        key: "nassau",
        type: "boolean",
        default: false,
        label: "Nassau (front 9 + back 9 + overall pots)",
      },
    ],
  },
];

// Convenience lookup. Catalog is small enough that a Map isn't worth
// the ceremony, but a function gets us memoized lookups on imports.
export function getGameDefinition(id: string): GameDefinition | undefined {
  return GAMES_CATALOG.find((g) => g.id === id);
}

// Default option payload for a given game id — used by GamesModal when
// the user toggles a game on for the first time and we need a starting
// option set. Returns {} if the game id isn't in the catalog.
export function defaultOptionsFor(
  id: string,
): Record<string, boolean | number | string> {
  const def = getGameDefinition(id);
  if (!def) return {};
  const out: Record<string, boolean | number | string> = {};
  for (const opt of def.options) out[opt.key] = opt.default;
  return out;
}
